/**
 * JWT utility functions for decoding tokens without signature verification
 */

export interface JwtPayload {
  exp?: number // Unix timestamp in seconds
  iat?: number
  user_id?: string | number
  [key: string]: any
}

/**
 * Custom error for expired refresh tokens
 */
export class RefreshExpiredError extends Error {
  constructor(message = 'Refresh token has expired') {
    super(message)
    this.name = 'RefreshExpiredError'
  }
}

/**
 * Decodes a JWT token payload without verifying the signature
 * This is safe for client-side use as the backend will verify the signature
 *
 * @param token - JWT token string
 * @returns Decoded payload object
 * @throws Error if token format is invalid
 */
export function decodeJwtPayload(token: string): JwtPayload {
  if (!token || typeof token !== 'string') {
    throw new Error('Invalid token: must be a non-empty string')
  }

  const parts = token.split('.')
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format: token must have 3 parts')
  }

  try {
    // Base64Url decode the payload (second part)
    const base64Url = parts[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    throw new Error(`Failed to decode JWT payload: ${error}`)
  }
}

/**
 * Extracts the expiration timestamp from a JWT token
 *
 * @param token - JWT token string
 * @returns Unix timestamp in seconds, or null if exp is not present
 */
export function getTokenExpiration(token: string): number | null {
  try {
    const payload = decodeJwtPayload(token)
    return payload.exp ?? null
  } catch {
    return null
  }
}

/**
 * Checks if a token is expired with optional leeway
 *
 * @param token - JWT token string
 * @param leewaySeconds - Leeway in seconds to account for clock skew (default: 0)
 * @returns true if token is expired (considering leeway), false otherwise
 */
export function isTokenExpired(token: string, leewaySeconds = 0): boolean {
  const exp = getTokenExpiration(token)
  if (exp === null) {
    return true // No expiration means we can't trust it
  }

  const nowSeconds = Math.floor(Date.now() / 1000)
  return nowSeconds >= exp - leewaySeconds
}
